import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { LogoIcon } from 'components/Icons/LogoIcon';

type Props = {
  image: string | null;
  title: string;
  body: string;
  onTryForFreeClick: () => void;
};

const AffiliateHeader = ({ image, title, body, onTryForFreeClick }: Props) => {
  const [isPinned, setIsPinned] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInnerRef = useRef<HTMLDivElement>(null);

  // Only the Try for free row is sticky: collapse it into a bar once it pins.
  // Tablet and phone: slide the bar away while scrolling down, back in on scroll up.
  useEffect(() => {
    const row = ctaRef.current;

    if (!row) {
      return;
    }

    let lastY = window.scrollY;

    const pin = () => {
      const pinned = row.getBoundingClientRect().top <= 0;
      const delta = window.scrollY - lastY;

      setIsPinned(pinned);
      if (!pinned || row.contains(document.activeElement)) {
        setIsHidden(false);
      } else if (Math.abs(delta) > 5) {
        setIsHidden(delta > 0);
      }
      lastY = window.scrollY;
    };

    window.addEventListener('scroll', pin, { passive: true });
    window.addEventListener('resize', pin);
    pin();

    return () => {
      window.removeEventListener('scroll', pin);
      window.removeEventListener('resize', pin);
    };
  }, []);

  // Both card halves draw one border beam centred on the whole card,
  // so share the measured heights with them.
  useEffect(() => {
    const header = headerRef.current;
    const card = cardRef.current;
    const ctaInner = ctaInnerRef.current;

    if (
      !header ||
      !card ||
      !ctaInner ||
      typeof ResizeObserver === 'undefined'
    ) {
      return;
    }

    const measure = () => {
      if (ctaRef.current?.dataset.pinned === 'true') {
        return;
      }
      header.style.setProperty('--aff-top-h', `${card.offsetHeight}px`);
      header.style.setProperty(
        '--aff-card-h',
        `${card.offsetHeight + ctaInner.offsetHeight}px`
      );
    };

    const observer = new ResizeObserver(measure);

    observer.observe(card);

    return () => observer.disconnect();
  }, []);

  // Tapping the collapsed bar (anywhere but the button) scrolls back to the full intro.
  const onBarClick = () => {
    if (isPinned) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const onTryClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onTryForFreeClick();
  };

  const avatar = (isMini: boolean) => (
    <Avatar $isMini={isMini}>
      <Pic>
        {image ? (
          <img src={image} alt={isMini ? '' : title} />
        ) : (
          <LogoIcon width={isMini ? 40 : 80} height={isMini ? 24 : 48} />
        )}
      </Pic>
    </Avatar>
  );

  return (
    <Container ref={headerRef}>
      <Intro>
        <Card ref={cardRef}>
          {avatar(false)}
          <Copy>
            <Title>{title}</Title>
            <Lead>{body}</Lead>
          </Copy>
        </Card>
      </Intro>
      <Cta
        ref={ctaRef}
        data-pinned={isPinned}
        $isPinned={isPinned}
        $isHidden={isHidden}
        onClick={onBarClick}
      >
        <CtaInner ref={ctaInnerRef} $isPinned={isPinned}>
          {isPinned && (
            <Mini aria-hidden="true">
              {avatar(true)}
              <MiniTitle>{title}</MiniTitle>
            </Mini>
          )}
          <Pill type="button" $isPinned={isPinned} onClick={onTryClick}>
            Try for free
          </Pill>
        </CtaInner>
      </Cta>
    </Container>
  );
};

const beam = keyframes`
  to {
    --aff-angle: 360deg;
  }
`;

const miniIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const Container = styled.header`
  display: contents;
  animation: ${beam} 7s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Intro = styled.div`
  padding: 20px 20px 0;
  background-color: ${({ theme }) => theme.colors.white};
`;

// Card inner border: a comet of light loops round a line inset 4px inside the card.
// Both halves draw their part of one conic gradient centred on the whole card.
const beamBorder = css`
  position: relative;
  isolation: isolate;
  background-color: ${({ theme }) => theme.colors.topicBlue};

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    border: 2px solid transparent;
    background: conic-gradient(
        from var(--aff-angle) at 50% var(--aff-cy),
        rgb(105 176 198 / 22%) 0deg,
        rgb(105 176 198 / 22%) 250deg,
        ${({ theme }) => theme.colors.darkBlueV2} 330deg,
        #d9eff6 356deg,
        rgb(105 176 198 / 22%) 360deg
      )
      border-box;
    mask:
      linear-gradient(#000 0 0) padding-box exclude,
      linear-gradient(#000 0 0);
    pointer-events: none;
  }
`;

const Card = styled.div`
  ${beamBorder}
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 40px 20px 0;
  border-radius: 8px 8px 0 0;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;

  &::before {
    inset: 4px 4px 0;
    border-bottom: 0;
    border-radius: 4px 4px 0 0;

    --aff-cy: calc(var(--aff-card-h, 400px) / 2 - 4px);
  }
`;

const Avatar = styled.span<{ $isMini: boolean }>`
  display: block;
  flex-shrink: 0;
  width: 144px;
  height: 144px;
  padding: 10px;
  box-sizing: border-box;
  border: 2px dashed #9dc3d3;
  border-radius: 50%;

  ${({ $isMini }) =>
    $isMini &&
    css`
      width: 72px;
      height: 72px;
      padding: 4px;
      border-color: ${({ theme }) => theme.colors.darkBlueV2};
    `}
`;

const Pic = styled.span`
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Title = styled.p`
  margin: 0;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.3px;
  text-wrap: balance;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 28px;
  }
`;

const Lead = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.4;
  text-wrap: balance;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 18px;
  }
`;

// Padding is rebalanced when pinned so the bar keeps its height and nothing below jumps.
const Cta = styled.div<{ $isPinned: boolean; $isHidden: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1000;
  padding: 0 20px 20px;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 0.5px solid rgb(0 0 0 / 10%);
  box-shadow: 0 14px 24px -10px rgb(70 83 111 / 20%);
  transition:
    background-color 0.25s,
    transform 0.25s;

  ${({ $isPinned }) =>
    $isPinned &&
    css`
      padding: 0;
      cursor: pointer;
      background-color: ${({ theme }) => theme.colors.sand};
    `}

  ${({ theme, $isHidden }) =>
    $isHidden &&
    css`
      ${theme.breakpoints.down('md')} {
        transform: translateY(calc(-100% - 20px));
      }
    `}
`;

const CtaInner = styled.div<{ $isPinned: boolean }>`
  ${beamBorder}
  padding: 20px 20px 30px;
  border-radius: 0 0 8px 8px;

  &::before {
    inset: 0 4px 4px;
    border-top: 0;
    border-radius: 0 0 4px 4px;

    --aff-cy: calc(var(--aff-card-h, 400px) / 2 - var(--aff-top-h, 300px));
  }

  ${({ $isPinned }) =>
    $isPinned &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 23px 29px;
      border-radius: 0;
      background: none;

      &::before {
        display: none;
      }

      ${({ theme }) => theme.breakpoints.down('md')} {
        padding: 23px 20px;
      }
    `}
`;

const Mini = styled.span`
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
  animation: ${miniIn} 0.25s ease both;
`;

const MiniTitle = styled.span`
  overflow: hidden;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.2px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.colors.topicBlue};

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const Pill = styled.button<{ $isPinned: boolean }>`
  display: block;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  padding: 14px 20px;
  border: 2px solid ${({ theme }) => theme.colors.darkBlueV2};
  border-radius: 30px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.darkBlueV2};
  box-shadow: 0 10px 20px rgb(0 0 0 / 10%);
  cursor: pointer;
  transition:
    background-color 0.15s,
    border-color 0.15s;

  &:hover {
    background-color: #5c9fb8;
    border-color: #5c9fb8;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: none;
  }

  ${({ $isPinned }) =>
    $isPinned &&
    css`
      flex-shrink: 0;
      width: auto;
      max-width: none;
      margin: 0;
      padding: 14px 28px;
    `}
`;

export default AffiliateHeader;
